import { useMutation } from '@tanstack/react-query';
import { fabric } from 'fabric';
import { Canvas } from 'fabric/fabric-impl';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/common/modal';
import { Button, ButtonVariant } from '@/components/form/button';
import { Stain } from '@/features/user-settings';
import { useSize } from '@/hooks';

import { updateBatch } from '../../services/batches';
import { updateTask } from '../../services/tasks';
import { IBatch, ITask, Method, State } from '../../types';
import { CellType } from '../../types/cell-type';

export interface IImageTask extends ITask {
  source?: string;

  result?: string;
}

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close state */
  toggle: () => void;

  task: IImageTask;

  batch: IBatch;
}

interface ICell {
  cellType: number;
  x: number;
  y: number;
  width: number;
  height: number;
  canvasLength: number;
}

const STROKE_WIDTH = 2;

function getStrokeColor(cellType: CellType, isViab: boolean, stain: Stain) {
  switch (cellType) {
    case CellType.Transparent:
      return isViab ? 'rgba(34, 177, 76, 1)' : 'rgba( 255 , 0 , 0 , 0)';
    case CellType.Stained:
      return stain === Stain.MethyleneBlue
        ? 'rgba(17, 126, 166, 1)'
        : 'rgba(170, 0, 255, 1)';
    case CellType.BuddingTransparent:
      return 'rgba(255, 214, 0, 1)';
    case CellType.BuddingStained:
      return 'rgba(255, 214, 0, 1)';
    case CellType.BuddingSmall:
      return 'rgba(136, 116, 11, 1)';
    case CellType.Brettanomyces:
      return 'rgba(255, 214, 0, 1)';
    default:
      return 'rgba(154, 154, 154, 1)';
  }
}

function getSecondaryStrokeColor(cellType: CellType) {
  switch (cellType) {
    case CellType.BuddingTransparent:
      return 'rgba(34, 177, 76, 1)';
    default:
      return 'transparent';
  }
}

const getOriginalXYCord = (scaledValue: number, canvasLength: number): number => {
  return (scaledValue + 1) / canvasLength;
};

const getOriginalDimension = (value: number, canvasLength: number): number => {
  return (2 * (value / 2)) / canvasLength;
};

export const TaskEditModal: React.FC<IProps> = ({ task, batch, isOpen, toggle }) => {
  const fabricRef = useRef<Canvas | null>(null);
  const canvasRef = useRef(null);

  // A hook to determine the size of canvas container
  const canvasContainerRef = useRef(null);
  const canvasContainerSize = useSize(canvasContainerRef);

  const stain = batch.methodAttributes.stain;
  const isViab = [
    Method.WineConzViab,
    Method.Yeast2ConzViab,
    Method.BioEthConzViab,
  ].includes(batch.method);
  const isBrett = [Method.Yeast2Brett, Method.WineBrett, Method.BioEthrett].includes(
    batch.method,
  );

  const [selectedCellType, setSelectedCellType] = useState<CellType | null>(null);

  // initialize the canvas with cells from task's detections
  useEffect(() => {
    const initFabric = () => {
      fabricRef.current = new fabric.Canvas(canvasRef.current);
    };

    const disposeFabric = () => {
      fabricRef.current && fabricRef.current.dispose();
    };

    initFabric();
    return () => {
      disposeFabric();
    };
  }, []);

  // once canvas mounted we place cells on it
  useEffect(() => {
    if (!canvasContainerSize?.width) return;

    // set size of the canvas based on it's container
    // we can't use CSS for canvas sizing
    const lengthOfCanvasSide = canvasContainerSize.width / 2;
    fabricRef.current?.setWidth(lengthOfCanvasSide);
    fabricRef.current?.setHeight(lengthOfCanvasSide);

    // set task's source image as the background of canvas
    fabric.Image.fromURL(task.sourceImage, function (img) {
      img.scaleToWidth(lengthOfCanvasSide);
      img.scaleToHeight(lengthOfCanvasSide);
      fabricRef.current?.setBackgroundImage(img, () => {});
      fabricRef.current?.requestRenderAll();
    });

    // add event listener to clear out cell type selection when there's no object selected in the canvas,
    // so that we can reset radio button selection
    fabricRef.current?.on('selection:cleared', () => {
      setSelectedCellType(null);
    });

    // draw all the pre-detected cells
    if (task && task.detections && task.detections?.length > 0) {
      fabricRef.current?.clear();

      for (const point of task.detections) {
        addCell(point[0], point[1], point[2], point[3], point[4]);
      }
    }

    drawDetectionBoundary(lengthOfCanvasSide);
  }, [canvasContainerSize]);

  const updateBatchMutation = useMutation({
    mutationFn: ({ id, batch }: { id: string; batch: Partial<IBatch> }) =>
      updateBatch(id, { ...batch }),
    onSuccess: () => {
      window.location.reload();
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({
      taskId,
      batchId,
      data,
    }: {
      taskId: string;
      batchId: string;
      data: any;
    }) => updateTask({ taskId, batchId, data }),
    onSuccess: () => {
      updateBatchMutation.mutate({
        id: batch.id,
        batch: { state: State.UploadCompleted },
      });
    },
  });

  const drawDetectionBoundary = (canvasLength: number) => {
    const circleOpts = {
      top: 0,
      left: 0,
      stroke: 'red',
      strokeWidth: 0.7,
      selectable: false,
      hasControls: false,
      fill: 'transparent',
      hoverCursor: 'default',
      radius: canvasLength / 2 - 1,
    };

    // white circle boundary
    const circle = new fabric.Circle({ ...circleOpts, stroke: 'white' });

    // red arcs on white circle
    const arcOpts = { startAngle: 0, endAngle: 22.5 };
    for (let i = 0; i < 5; i++) {
      const redArc = new fabric.Circle({
        ...circleOpts,
        stroke: 'red',
        startAngle: arcOpts.startAngle,
        endAngle: arcOpts.endAngle,
      });
      fabricRef.current?.add(redArc);
      redArc.sendToBack();
      arcOpts.startAngle = arcOpts.endAngle + 45;
      arcOpts.endAngle = arcOpts.endAngle + 90;
    }
    fabricRef.current?.add(circle);
    circle.sendToBack();
  };

  const addRoundCellGroup = ({
    x,
    y,
    height,
    width,
    cellType,
    canvasLength,
  }: ICell): fabric.Group => {
    const ellipseOpt = {
      strokeWidth: STROKE_WIDTH,
      selectable: false,
      strokeUniform: true,
      top: y * canvasLength - 1,
      left: x * canvasLength - 1,
      rx: (width * canvasLength) / 2,
      ry: (height * canvasLength) / 2,
    };

    const cell = new fabric.Ellipse({
      ...ellipseOpt,
      fill: 'rgba(0, 0, 0, 0.2)',
      stroke: getStrokeColor(cellType, isViab, stain),
    });

    const shadow = new fabric.Ellipse({
      ...ellipseOpt,
      fill: 'transparent',
      strokeDashArray: [5, 5],
      stroke: getSecondaryStrokeColor(cellType),
    });

    const group = new fabric.Group([cell, shadow], {
      data: { cellType, x, y, width, height },
      cornerSize: 7,
      selectable: true,
      borderColor: '#028bfb',
      transparentCorners: false,
      cornerStrokeColor: '#028bfb',
    });

    // update the shadow radius based on the main object's scale
    group.on('scaling', function () {
      shadow.set({
        rx: cell.rx! * cell.scaleX!,
        ry: cell.ry! * cell.scaleY!,
      });
    });

    return group;
  };

  const addBrettCell = ({
    x,
    y,
    height,
    width,
    cellType,
    canvasLength,
  }: ICell): fabric.Group => {
    const cell = new fabric.Rect({
      width: width * canvasLength,
      height: height * canvasLength,
      selectable: false,
      strokeUniform: true,
      fill: 'rgba(0, 0, 0, 0.2)',
      stroke: getStrokeColor(cellType, isViab, stain),
      strokeWidth: 2,
      top: y * canvasLength - 1,
      left: x * canvasLength - 1,
    });

    const group = new fabric.Group([cell], {
      data: { cellType, x, y, width, height },
      cornerSize: 7,
      selectable: true,
      borderColor: '#028bfb',
      transparentCorners: false,
      cornerStrokeColor: '#028bfb',
    });

    return group;
  };

  // add cells into canvas
  const addCell = (
    cellType: number,
    x: number,
    y: number,
    width: number,
    height: number,
  ) => {
    if (!fabricRef.current) return;

    const canvasLength = fabricRef.current.getWidth();

    const group = isBrett
      ? addBrettCell({ x, y, cellType, width, height, canvasLength })
      : addRoundCellGroup({ x, y, cellType, width, height, canvasLength });

    // add event listener to modify cell type radio buttons "checked" state
    group.on('mousedown', (e) => {
      setSelectedCellType(e.target?.data.cellType);
    });

    // remove object rotation
    group.setControlsVisibility({ mtr: false });

    fabricRef.current.add(group);
    fabricRef.current.renderAll();

    return group;
  };

  const handleOnSave = () => {
    if (!fabricRef.current) return;
    const canvasLength = fabricRef.current.getWidth();

    // we construct the modified detections based on the canvas
    const newDetections: number[][] = [];
    for (const cell of fabricRef.current.getObjects()) {
      if (cell.data?.cellType === undefined) continue;
      const point = cell.getCoords()[0];
      newDetections.push([
        cell.data?.cellType,
        getOriginalXYCord(point.x, canvasLength),
        getOriginalXYCord(point.y, canvasLength),
        getOriginalDimension(cell.getScaledWidth() - STROKE_WIDTH, canvasLength),
        getOriginalDimension(cell.getScaledHeight() - STROKE_WIDTH, canvasLength),
      ]);
    }

    updateTaskMutation.mutate({
      batchId: batch.id,
      taskId: task.id,
      data: {
        detections: `detections: ${JSON.stringify(newDetections)}`,
      },
    });
  };

  const removeActiveCell = () => {
    if (fabricRef.current && fabricRef.current.getActiveObject()) {
      fabricRef.current.remove(fabricRef.current.getActiveObject()!);
    }
  };

  const setActiveCell = (cell: fabric.Object, cellType: CellType) => {
    if (!fabricRef.current) return;

    if (cell) {
      setSelectedCellType(cellType);
      fabricRef.current.setActiveObject(cell);
      fabricRef.current.renderAll();
    }
  };

  const changeActiveCellType = (cellType: CellType) => {
    if (fabricRef.current && fabricRef.current.getActiveObject()) {
      const canvasLength = fabricRef.current.getWidth();

      const { x, y } = fabricRef.current.getActiveObject()!.getCoords()[0];
      const width = fabricRef.current.getActiveObject()?.getScaledWidth() || 0;
      const height = fabricRef.current.getActiveObject()?.getScaledHeight() || 0;

      const currentX = getOriginalDimension(x + 1, canvasLength);
      const currentY = getOriginalDimension(y + 1, canvasLength);
      const currentWidth = getOriginalDimension(width - STROKE_WIDTH, canvasLength);
      const currentHeight = getOriginalDimension(height - STROKE_WIDTH, canvasLength);

      // remove selected cell
      removeActiveCell();

      // create new cell with new cell type
      const cell = addCell(cellType, currentX, currentY, currentWidth, currentHeight);

      // re-render the canvas and activate newly created cell
      if (cell) setActiveCell(cell, cellType);
    }
  };

  const addNewActiveCell = () => {
    if (!fabricRef.current) return;

    // add a new cell with default settings
    const cellType = isBrett ? CellType.Brettanomyces : CellType.Transparent;
    const cell = addCell(cellType, 0.4, 0.4, 0.09, 0.09);

    // re-render the canvas and activate newly created cell
    if (cell) setActiveCell(cell, CellType.Transparent);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      title="Edit Cells"
      conatinerClassnames="w-full md:w-5/6"
    >
      <div className="flex gap-10 px-10 py-10" ref={canvasContainerRef}>
        <div>
          <canvas width={canvasContainerSize?.width} ref={canvasRef} />
        </div>
        <div className="flex flex-col justify-between">
          <div className="flex flex-col gap-8">
            <div className="flex gap-4">
              <Button variant={ButtonVariant.Secondary} onClick={addNewActiveCell}>
                Add New Cell
              </Button>
              <Button
                variant={ButtonVariant.Secondary}
                disabled={selectedCellType === null}
                onClick={removeActiveCell}
              >
                Remove Cell
              </Button>
            </div>
            {!isBrett ? (
              <div className="flex flex-col gap-4 pl-2">
                <h1>Cell Category</h1>
                <RadioInput
                  name="cellType"
                  label="Transparent Cell"
                  value={CellType.Transparent.toString()}
                  checked={selectedCellType === CellType.Transparent}
                  onChange={() => changeActiveCellType(CellType.Transparent)}
                />
                <RadioInput
                  name="cellType"
                  label="Stained Cell"
                  value={CellType.Stained.toString()}
                  checked={selectedCellType === CellType.Stained}
                  onChange={() => changeActiveCellType(CellType.Stained)}
                />
                <RadioInput
                  name="cellType"
                  label="Budding Cell - Transparent"
                  value={CellType.BuddingTransparent.toString()}
                  checked={selectedCellType === CellType.BuddingTransparent}
                  onChange={() => changeActiveCellType(CellType.BuddingTransparent)}
                />
                <RadioInput
                  name="cellType"
                  label="Budding Cell - Stained"
                  value={CellType.BuddingStained.toString()}
                  checked={selectedCellType === CellType.BuddingStained}
                  onChange={() => changeActiveCellType(CellType.BuddingStained)}
                />
                <RadioInput
                  name="cellType"
                  label="Budding Cell - Not counted"
                  value={CellType.BuddingSmall.toString()}
                  checked={selectedCellType === CellType.BuddingSmall}
                  onChange={() => changeActiveCellType(CellType.BuddingSmall)}
                />
              </div>
            ) : null}
          </div>
          <div>
            <Button onClick={handleOnSave}>Save</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const RadioInput = ({
  name,
  label,
  value,
  onChange,
  checked = false,
}: {
  name: string;
  label: string;
  value: string;
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-4">
      <input
        name={name}
        type="radio"
        value={value}
        checked={checked}
        onChange={(e) => onChange && onChange(e)}
        id={`${name}-${value}`}
        className="cursor-pointer"
      />
      <label className="text-gray-700 cursor-pointer" htmlFor={`${name}-${value}`}>
        {t(label)}
      </label>
    </div>
  );
};
